import { useState } from "react";
import { toast } from "sonner";
import { Save, Trash2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { fmtDate } from "../lib/fmt";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const STATUS_OPTS = [["ontrack", "On track"], ["delay", "Delay"], ["blocked", "Blocked"]];

const Text = ({ id, label, value, onChange, rows = 3 }) => (
  <div>
    <label className="label" htmlFor={id}>{label}</label>
    <textarea id={id} data-testid={id} rows={rows} className="field resize-y" value={value} onChange={(e) => onChange(e.target.value)} />
  </div>
);

function kpiFieldsFor(data, r) {
  const fn = data.jobFunctions.find((f) => f.name === r.role);
  const fields = [...(fn?.kpi || [])];
  Object.keys(r.kpi || {}).forEach((k) => { if (!fields.find((x) => x.key === k)) fields.push({ key: k, label: k }); });
  return fields;
}

function AdminForm({ r, data, ownerName, onDone }) {
  const [f, setF] = useState({
    status: r.status, activity: r.activity || "", metrics: r.metrics || "", blocker: r.blocker || "",
    tomorrow: r.tomorrow || "", program: r.program || "",
    kpi: Object.fromEntries(Object.entries(r.kpi || {}).map(([k, v]) => [k, String(v)])),
  });
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.put(`/admin/reports/${r.id}`, f);
      toast.success(res.data.changed.length ? `Laporan ${ownerName} diperbarui` : "Tidak ada perubahan");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/reports/${r.id}`);
      toast.success(`Laporan ${r.code} dihapus`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-3" data-testid="admin-report-form">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label" htmlFor="admin-status">Status</label>
          <select id="admin-status" data-testid="admin-status" className="field" value={f.status} onChange={(e) => set("status")(e.target.value)}>
            {STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="admin-program">Program</label>
          <select id="admin-program" data-testid="admin-program" className="field" value={f.program} onChange={(e) => set("program")(e.target.value)}>
            <option value="">— Tanpa —</option>
            {[...new Set([...(data.programs || []), ...(f.program ? [f.program] : [])])].map((pg) => <option key={pg} value={pg}>{pg}</option>)}
          </select>
        </div>
      </div>
      <Text id="admin-activity" label="Aktivitas (satu per baris)" value={f.activity} onChange={set("activity")} rows={4} />
      {kpiFieldsFor(data, r).length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {kpiFieldsFor(data, r).map((k) => (
            <div key={k.key}>
              <label className="mb-1 block text-xs text-[#7a6667]" htmlFor={`admin-kpi-${k.key}`}>{k.label}</label>
              <input id={`admin-kpi-${k.key}`} data-testid={`admin-kpi-${k.key}`} className="field tabular-nums" inputMode="numeric" placeholder="–"
                value={f.kpi[k.key] || ""} onChange={(e) => setF((p) => ({ ...p, kpi: { ...p.kpi, [k.key]: e.target.value } }))} />
            </div>
          ))}
        </div>
      )}
      <div>
        <label className="label" htmlFor="admin-metrics">Catatan angka lain</label>
        <input id="admin-metrics" data-testid="admin-metrics" className="field" value={f.metrics} onChange={(e) => set("metrics")(e.target.value)} />
      </div>
      <Text id="admin-blocker" label="Blocker" value={f.blocker} onChange={set("blocker")} />
      <Text id="admin-tomorrow" label="Rencana besok" value={f.tomorrow} onChange={set("tomorrow")} />
      <p className="text-xs text-[#7a6667]">Kode {r.code} dan jam kirim tidak berubah. Versi lama disimpan di riwayat dan tercatat di log aktivitas.</p>
      <button data-testid="admin-save" disabled={busy} onClick={save} className="btn-primary"><Save size={16} /> Simpan perubahan</button>
      {!confirmDel ? (
        <button data-testid="admin-delete" disabled={busy} onClick={() => setConfirmDel(true)} className="btn-ghost text-brand"><Trash2 size={15} /> Hapus laporan ini</button>
      ) : (
        <div className="rounded-xl border border-brand bg-brand-soft p-3 text-sm" data-testid="admin-delete-confirm">
          <p className="font-semibold">Hapus laporan {ownerName} tanggal {fmtDate(r.date)}? Salinannya tetap disimpan di riwayat.</p>
          <div className="mt-2 flex gap-2">
            <button data-testid="admin-delete-yes" disabled={busy} onClick={remove} className="btn-primary flex-1">Ya, hapus</button>
            <button data-testid="admin-delete-no" onClick={() => setConfirmDel(false)} className="btn-ghost">Batal</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminReportDialog({ report, data, ownerName, onClose, onDone }) {
  return (
    <Dialog open={!!report} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="admin-report-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Kelola laporan {ownerName}</DialogTitle>
          <DialogDescription>{report ? `${fmtDate(report.date)} · ${report.code}` : ""}</DialogDescription>
        </DialogHeader>
        {report && <AdminForm key={report.id} r={report} data={data} ownerName={ownerName} onDone={() => { onDone(); onClose(); }} />}
      </DialogContent>
    </Dialog>
  );
}
